import { CustomerMeetingUserState } from '@libs/focus-frontend-types/src/@teamleader/api/customerMeetingUsers';

import privacyAndTermsUrls from '../../static/privacyAndTermsUrls.json';
import { Permission, User, canAccessPlanningPreview, getAuthenticatedUser } from '../AuthenticationService';
import { AccountFeatureFlag, FeatureFlag, getAccountFeatureFlag, get as getFeatureFlag } from '../FeatureFlagService';
import PreferencesService from '../PreferencesService';
import { SessionStorageKey, getPreferenceFromSessionStorage } from '../SessionStorageService';

import { MobilePlatform } from './@types';

export const root = (parameterString = '') => `/${parameterString}`;
export const appRoot = () => environment.APP_URL;

export const backendRoot = () => environment.BACKEND_URL;

export const statusPage = () => 'https://status.teamleader.eu';

export const manageSubscription = () => {
  return '/web/settings/subscription';
};

export const subscriptionDetail = (subscriptionId: string) => `/subscription_detail.php?id=${subscriptionId}`;

export const creditNoteDetail = (creditNoteId: string) => `/creditnote_detail.php?id=${creditNoteId}`;

export const upgradeDemoAccount = () => {
  return '/web/settings/subscription/start';
};

export const startPackageTrial = (packageId = '') => {
  packageId = packageId.startsWith('/') ? packageId : '/' + packageId;
  return `/web/settings/subscription/trial${packageId.toLowerCase()}`;
};

export const startFeatureTrial = (featureId: string) => {
  return `/web/settings/subscription/feature-trial/${featureId.toLowerCase()}`;
};

export const customerMeetingSsoStandalone = (language: string, next?: string) => {
  const baseUrl = `${environment.VECTERA_URL}/social-accounts/teamleader-login/?lang=${language}`;

  if (next) {
    return `${baseUrl}&next=${next}`;
  }

  return baseUrl;
};
export const customerMeetingContactForms = (language: string) =>
  customerMeetingSsoStandalone(language, '/contact-forms/add');
export const customerMeetingContactFormsForActivatedUsers = (language: string, skipWelcome = false) =>
  customerMeetingSsoStandalone(language, '/contact-forms' + (skipWelcome ? '/?skipWelcome' : ''));
export const customerMeetingContactFormsInAppPage = () => `/contact-forms`;
export const customerMeetingPromotionalPage = (next?: string) => {
  const baseUrl = `/try-customer-meeting`;

  if (next) {
    return `${baseUrl}?next=${next}`;
  }

  return baseUrl;
};
export const customerMeetingLearnMore = (language: string) => {
  switch (language) {
    case 'nl-BE':
      return 'https://www.teamleader.eu/nl-be/s/customer-meeting-add-on';
    case 'fr-BE':
      return 'https://www.teamleader.eu/fr-be/s/customer-meeting-add-on';
    case 'de-DE':
      return 'https://www.teamleader.eu/de/s/customer-meeting-add-on';
    case 'fr-FR':
      return 'https://www.teamleader.eu/fr/s/customer-meeting-add-on';
    case 'it-IT':
      return 'https://www.teamleader.eu/it/s/customer-meeting-add-on';
    case 'nl-NL':
      return 'https://www.teamleader.eu/nl/s/customer-meeting-add-on';
    case 'es-ES':
      return 'https://www.teamleader.eu/es/s/customer-meeting-add-on';
    default:
      return 'https://www.teamleader.eu/s/customer-meeting-add-on';
  }
};

export const customerMeeting = (state: CustomerMeetingUserState, language: string) =>
  state === CustomerMeetingUserState.Active ? customerMeetingSsoStandalone(language) : customerMeetingPromotionalPage();
export const upgradeCustomerMeetingTrial = () => '/web/settings/subscription/customer-meeting/upgrade';
export const addCustomerMeetingUsers = () => '/web/settings/subscription/customer-meeting/add-seats';

export const marketplace = () => environment.MARKETPLACE_URL;
export const marketplaceBuild = () => `${marketplace()}/build`;
export const marketplaceIntegration = (reference: string) => `${marketplace()}/integration/${reference}`;
export const marketplaceLogin = () => `${marketplace()}/login`;
export const marketplaceManage = () => `${marketplace()}/manage`;
export const marketplaceCategory = (category: string) => `${marketplace()}/eu/en/category/${category}`;

export const dashboard = (parameterString = '') => {
  const isDashingDashboardFeatureFlagEnabled = getAccountFeatureFlag(AccountFeatureFlag.DashingDashboard, false);

  if (isDashingDashboardFeatureFlagEnabled) {
    return `/dashboard${parameterString}`;
  }

  return `/dashboard.php${parameterString}`;
};
export const calendar = (parameterString = '') => `/planning.php${parameterString}`;

export const invoiceOverview = (parameterString = '') => `/invoices.php${parameterString}`;
export const invoiceDetail = (id: string) => `/invoice_detail.php?id=${id}`;
export const invoiceForecast = () => '/web/invoicing/invoices/forecast';

export const incomingDocumentsOverview = () => `/incoming-documents`;
export const expensesOverview = () => `/expenses`;

export const incomingInvoicesAdd = (isCostFfOn: boolean) =>
  isCostFfOn ? `${expensesOverview()}/incoming-invoices/new` : `/incoming-documents/incoming-invoices/new`;
export const incomingInvoicesEdit = (parameterString = '', isCostFfOn: boolean) =>
  isCostFfOn
    ? `${expensesOverview()}/incoming-invoices/${parameterString}/edit`
    : `/incoming-documents/incoming-invoices/${parameterString}/edit`;
export const incomingInvoicesDetail = (parameterString = '', isCostFfOn: boolean) =>
  isCostFfOn
    ? `${expensesOverview()}/incoming-invoices/${parameterString}`
    : `/incoming-documents/incoming-invoices/${parameterString}`;
export const incomingCreditNotesAdd = (isCostFfOn: boolean) =>
  isCostFfOn ? `${expensesOverview()}/incoming-credit-notes/new` : `/incoming-documents/incoming-credit-notes/new`;
export const incomingCreditNotesEdit = (parameterString = '', isCostFfOn: boolean) =>
  isCostFfOn
    ? `${expensesOverview()}/incoming-credit-notes/${parameterString}/edit`
    : `/incoming-documents/incoming-credit-notes/${parameterString}/edit`;
export const incomingCreditNotesDetail = (parameterString = '', isCostFfOn: boolean) =>
  isCostFfOn
    ? `${expensesOverview()}/incoming-credit-notes/${parameterString}`
    : `/incoming-documents/incoming-credit-notes/${parameterString}`;
export const receiptsAdd = (isCostFfOn: boolean) =>
  isCostFfOn ? `${expensesOverview()}/receipts/new` : '/incoming-documents/receipts/new';
export const receiptsEdit = (parameterString = '', isCostFfOn: boolean) =>
  isCostFfOn
    ? `${expensesOverview()}/receipts/${parameterString}/edit`
    : `/incoming-documents/receipts/${parameterString}/edit`;
export const receiptsDetail = (parameterString = '', isCostFfOn: boolean) =>
  isCostFfOn ? `${expensesOverview()}/receipts/${parameterString}` : `/incoming-documents/receipts/${parameterString}`;
export const placeholderDocumentsAdd = (parameterString = '') => `${expensesOverview()}/new/${parameterString}`;

export const dealOverview = (parameterString = '') => `/sales.php${parameterString}`;
export const dealPipeline = (parameterString = '') => `/web/deals/pipeline${parameterString}`;
export const dealDetail = (id: string, parameterString = '') => `/web/deals/${id}${parameterString}`;
export const dealForecast = (parameterString = '') => `/web/deals/forecast/${parameterString}`;

export const insightsFavourites = () => `/insights/favourites`;
export const insightsCreatedByMe = () => `/insights/created-by-me`;
export const insightsSharedWithMe = () => `/insights/shared-with-me`;
export const insightsLibrary = (category?: string) => `/insights${category ? `?category=${category}` : ''}`;
export const insightsDetail = (parameterString = '') => `/insights/reports/${parameterString}`;
export const insightsLibraryReportDetail = (id: string) => `/insights/library-reports/${id}`;

export const quotationOverview = (parameterString = '') => `/quotations/overview${parameterString}`;
export const quotationDetail = (parameterString = '') => `/quotations/${parameterString}`;

export const contactOverview = (parameterString = '') => `/contacts.php${parameterString}`;
export const contactDetail = (id: string) => `/contacts/${id}`;
export const contactDetailCustomise = (id: string) => `/contacts/${id}/customise`;

export const companyOverview = (parameterString = '') => `/companies.php${parameterString}`;
export const companyDetail = (id: string) => `/companies/${id}`;
export const companyDetailCustomise = (id: string) => `/companies/${id}/customise`;

export const taskOverview = () => `/tasks.php`;
export const taskDetail = (taskId: string) => `/task_detail.php?id=${taskId}`;

export const ticketOverview = (parameterString = '') => `/tickets.php${parameterString}`;
export const ticketDetail = (ticketId: string) => `/ticket_detail.php?id=${ticketId}`;

export const meetingDetail = (meetingId: string) => `/meeting_detail.php?id=${meetingId}`;

export const productOverview = (parameterString = '') => `/products.php${parameterString}`;
export const productDetail = (productId: string) => `/product_detail.php?id=${productId}`;

export const legacyApiWebhooks = () => '/apiwebhooks.php';

export const timesheetOverview = (parameterString = '') => `/timesheets.php${parameterString}`;
export const timesheetProjectDetail = (projectId: string, groupId?: string) =>
  `/timesheets_detail.php?project_id=${projectId}${groupId ? `&group_id=${groupId}` : ''}`;

export const workorderOverview = (parameterString = '') => `web/workorders${parameterString}`;
export const workorderDetail = (workorderId: string) => `web/workorders/${workorderId}`;

export const mailingOverview = () => '/mailing.php';

export const fileHistory = (fileId: string) => `/file_history.php?id=${fileId}`;

export const settings = (parameterString = '') => `/settings.php${parameterString}`;
export const documentLayouts = (departmentId?: string) => {
  const path = 'settings/document-layouts';

  if (departmentId) {
    return `${path}/${departmentId}`;
  }

  return path;
};
export const hrUserSettings = (tab?: 'active' | 'invited' | 'deactivated') => {
  return tab && tab !== 'active' ? `/settings/users/${tab}` : '/settings/users';
};
export const hrUserSettingsEmployee = (employeeId: string) =>
  getAccountFeatureFlag(AccountFeatureFlag.EnableReactifiedUserDetailPage)
    ? `/settings/users/${employeeId}/info`
    : `/employee_detail.php?id=${employeeId}`;
export const hrUserSettingsPermissions = (employeeId: string) => `/settings/users/${employeeId}/permissions`;

export const exportsList = () => 'exports/list';

export const cloudPlatformSettings = () => '/settings.php#cloudplatforms';
export const salesSettings = (parameterString = '') => `/settings.php${parameterString}#nav_sales`;
export const invoiceSettings = () => '/settings.php#nav_invoices';
export const currencySettings = () => '/settings/currency';
export const expensesSettings = () => '/settings/expenses';
export const walletSettings = () => '/web/settings/wallet';
export const companyInformationSettings = () => '/settings.php#company_information';

export const departmentLogo = (id: string) =>
  `${backendRoot()}/ajax/getDepartmentLogo.php?noFallback=true&id=${encodeURIComponent(id)}`;

export const projectOverview = (parameterString = '') => {
  if (getAuthenticatedUser(User.Permissions).includes(Permission.NextgenProjects)) {
    return '/projects';
  }

  if (!getFeatureFlag(FeatureFlag.ProjectsTimeline)) {
    return `/projects.php${parameterString}`;
  }
  return PreferencesService.get(['webApp', 'project_overview']) === 'timeline'
    ? `/web/projects/timeline${parameterString}`
    : `/projects.php${parameterString}`;
};

export const projectDetail = (projectId: string, phaseId: string | null = null) => {
  if (getAuthenticatedUser(User.Permissions).includes(Permission.NextgenProjects)) {
    return `/projects/${projectId}`;
  }

  if (!getFeatureFlag(FeatureFlag.WorkBreakdownFrontAndCentre)) {
    return `/project_detail.php?id=${projectId}`;
  }

  if (phaseId) {
    return `/web/projects/${projectId}/work-breakdown?sidePanelEntityId=${phaseId}&sidePanelType=taskGroupView&expandedTaskGroupId=${phaseId}`;
  }

  return `/web/projects/${projectId}/work-breakdown`;
};

export const legacyProjectDetail = (projectId: string) => `/web/projects/${projectId}/work-breakdown`;

export const convertQuotationToProject = (quotationId: string) => `/quotations/${quotationId}/convert-to-project`;
export const convertQuotationFromDealToProject = (quotationId: string, dealId: string) =>
  `/web/deals/${dealId}/convert-quotation-to-project?quotationId=${quotationId}`;

export const developerPortalUrl = () => environment.DEVELOPER_PORTAL_URL;
export const developerPortalIntegrationsOverview = () => `${environment.DEVELOPER_PORTAL_URL}/integrations`;
export const accountDetail = (id: string) => `/teamleader_group_detail.php?id=${id}`;
export const officeIntegrationsOverview = () => `/admin/integrations`;
export const officeIntegrationsDetail = (id: string) => `/admin/integrations/${id}`;

export const downloadExport = (uuid: string) => `/app/exports/${uuid}/download`;

export const changeLicense = () => `/web/settings/subscription/change`;
export const changePaymentMethod = () => '/web/settings/subscription/payment/edit';

export const profile = () => '/myaccount.php';
export const referrals = () => '/referrals.php';

export const legacyProjectsOverview = () => `/projects.php`;
export const legacyProjectsTimeline = () => `/web/projects/timeline`;
export const projectsOverview = (parameterString = '') => `/projects${parameterString}`;
export const projectsTimeline = (parameterString = '') => `/projects/timeline${parameterString}`;
export const projectsWorkBreakdown = (id: string, parameterString = '') =>
  `/projects/${id}/work-breakdown${parameterString}`;
export const projectsTimeTrackings = (id: string) => `/projects/${id}/time-trackings`;
export const projectsInfo = (id: string) => `/projects/${id}/info`;
export const projectsInvoices = (id: string, createNew = false) =>
  `/projects/${id}/invoices${createNew ? '?create' : ''}`;
export const projectNotFound = () => '/projects?item_not_found';
export const projectsMigration = () => '/projects/migrate';
export const projectsKanban = (id: string, parameterString = '') => `/projects/${id}/kanban${parameterString}`;

export const planningPeople = (parameterString?: string) => {
  const date = getPreferenceFromSessionStorage(SessionStorageKey.PlanningDate, null);
  const range = getPreferenceFromSessionStorage(SessionStorageKey.PlanningDateRange, null);

  const params = new URLSearchParams(parameterString);
  const baseUrl = canAccessPlanningPreview() ? 'planning/preview' : 'planning/people';

  if (date && !params.has('date')) {
    params.append('date', date);
  }

  if (range && !params.has('range')) {
    params.append('range', range);
  }

  if (params.size) {
    return `${baseUrl}?${params.toString()}`;
  }

  return baseUrl;
};

export const filePermalink = (fileId: string) => `/file_permalink.php?file_id=${fileId}`;

const getPrivacyAndTermsUrl = (type: 'dpa' | 'termsOfService' | 'privacy' | 'cookiePolicy' | 'subProcessors') =>
  (privacyAndTermsUrls[window.TLGLOBALS.LANGUAGE as keyof typeof privacyAndTermsUrls] || privacyAndTermsUrls['en'])[
    type
  ];
export const dpa = () => getPrivacyAndTermsUrl('dpa');
export const termsOfService = () => getPrivacyAndTermsUrl('termsOfService');
export const privacyPolicy = () => getPrivacyAndTermsUrl('privacy');
export const cookiePolicy = () => getPrivacyAndTermsUrl('cookiePolicy');
export const subProcessors = () => getPrivacyAndTermsUrl('subProcessors');
export const eula = () => 'https://www.teamleader.eu/eula';

export const knowledgeBase = (utmCampaign?: string, utmMedium?: string, utmSource?: string) => {
  const url = new URL('https://support.focus.teamleader.eu/hc');
  if (utmCampaign) {
    url.searchParams.append('utm_campaign', utmCampaign);
  }
  if (utmMedium) {
    url.searchParams.append('utm_medium', utmMedium);
  }
  if (utmSource) {
    url.searchParams.append('utm_source', utmSource);
  }

  return url.href;
};

const knowledgeBaseUrl = (path: string) =>
  `https://support.focus.teamleader.eu/hc${path.startsWith('/') ? path : `/${path}`}`;
const knowledgeBaseFolder = (folderId: string) => knowledgeBaseUrl(`/sections/${folderId}`);
const knowledgeBaseArticle = (articleId: string, utmCampaign: string = 'app+knowledgebase+views') =>
  knowledgeBaseUrl(`/articles/${articleId}?utm_campaign=${utmCampaign}`);
const knowledgeBaseCategory = (categoryId: string) => knowledgeBaseUrl(`/categories/${categoryId}`);
const knowledgeBaseTicketForm = (ticketFormId: string, utmCampaign: string = 'app+knowledgebase+views') =>
  knowledgeBaseUrl(`requests/new?ticket_form_id=${ticketFormId}&utm_campaign=${utmCampaign}`);

const knowledgeBaseArticleForIntroStates = (articleId: string) => knowledgeBaseArticle(articleId, 'app+intro+states');

export const generalKnowledgeBaseTicketForm = () => knowledgeBaseTicketForm('25911134514065');

export const createContactFormsKnowledgeBase = () => knowledgeBaseArticle('25695775141649');
export const companiesKnowledgeBase = () => knowledgeBaseFolder('24865550165393');
export const contactsKnowledgeBase = () => knowledgeBaseFolder('24865550165393');
export const tasksKnowledgeBase = () => knowledgeBaseFolder('25085616010257');
export const callsKnowledgeBase = () => knowledgeBaseFolder('25085616010257');
export const meetingsKnowledgeBase = () => knowledgeBaseFolder('25085616010257');
export const dealsKnowledgeBase = () => knowledgeBaseFolder('25690662577169');
export const projectsKnowledgeBase = () => knowledgeBaseFolder('25690691571089');
export const nextgenProjectsKnowledgeBase = () => knowledgeBaseFolder('25540902870545');
export const insightsLibraryKnowledgeBase = () => knowledgeBaseArticle('28006164415889');
export const insightsLibraryOldInsightsKnowledgeBase = () => knowledgeBaseArticle('30267723452945');
export const insightsAdvancedSharingKnowledgeBase = () => knowledgeBaseArticle('34775138522513');
export const insightsAdvancedFormattingKnowledgeBase = () => knowledgeBaseArticle('34775161744529');
export const insightsAdvancedFormulasKnowledgeBase = () => knowledgeBaseArticle('34775166155281');
export const insightsAdvancedComparisonChartsKnowledgeBase = () => knowledgeBaseArticle('34775243105681');
export const invoicesKnowledgeBase = () => knowledgeBaseFolder('25690685806097');
export const ticketsKnowledgeBase = () => knowledgeBaseFolder('25690708387601');
export const productsKnowledgeBase = () => knowledgeBaseFolder('25690662617361');
export const timesheetsKnowledgeBase = () => knowledgeBaseFolder('25690699417233');
export const privacyAndTermsLearnMoreKnowledgeBase = () => knowledgeBaseFolder('25690708572305');
export const creditNotesKnowledgeBase = () => knowledgeBaseArticle('25692421867409');
export const subscriptionsKnowledgeBase = () => knowledgeBaseArticle('25691389220881');
export const deliveryNotesKnowledgeBase = () => knowledgeBaseArticle('25691460255505');
export const ordersKnowledgeBase = () => knowledgeBaseArticle('25691460255505');
export const orderConfirmationsKnowledgeBase = () => knowledgeBaseArticle('25691460255505');
export const defineInternalCostsOfEmployeesKnowledgeBase = () => knowledgeBaseArticle('25693477361681');
export const howToUsePurchasePriceOnOrdersKnowledgeBase = () => knowledgeBaseArticle('25693484224273');
export const mfaKnowledgeBase = () => knowledgeBaseArticle('25692015506577');
export const mfaEnforcementKnowledgeBase = () => knowledgeBaseArticle('25695959602833');
export const whyAmILoggedOutKnowledgeBase = () => knowledgeBaseArticle('25695405706385');
export const privacyAndTermsUpdate2024H1KnowledgeBase = () => knowledgeBaseArticle('25696103129745');
export const expiryDateOnQuotationsKnowledgeBase = () => knowledgeBaseArticle('25695533505553');
export const multipleCompanyEntitiesKnowledgeBase = () => knowledgeBaseArticle('25697681388177');
export const multipleDealPipelinesKnowledgeBase = () => knowledgeBaseArticle('25696017218705');
export const weeklyTimeTrackingKnowledgeBase = () => knowledgeBaseArticle('25693832046865');
export const belgianLateFeesComplianceKnowledgeBase = () => knowledgeBaseArticle('25695888942609');
export const poNumberKnowledgeBase = () => knowledgeBaseArticle('25692069252497');
export const projectsOverviewKnowledgeBase = () => knowledgeBaseArticle('25695851989905');
export const projectsBillingMethodKnowledgeBase = () => knowledgeBaseArticle('25695653109393');
export const peppolKnowledgeBase = () => knowledgeBaseArticle('25693204310673');
export const peppolKnowledgeAlreadyRegisteredBase = () => knowledgeBaseArticle('30082880993553');
export const faqPeppolKnowledgeBase = () => knowledgeBaseArticle('30083675838609');
export const activityTypesKnowledgeBase = () => knowledgeBaseArticle('25691899451153');
export const callOutcomesKnowledgeBase = () => knowledgeBaseArticle('25691879792273');
export const emailTemplatesKnowledgeBase = () => knowledgeBaseArticle('25691228260881');
export const companyDetailsKnowledgeBase = () => knowledgeBaseArticle('25694847994769');
export const termsAndConditionsKnowledgeBase = () => knowledgeBaseArticle('25690882757137');
export const tagsKnowledgeBase = () => knowledgeBaseArticle('25690854640529');
export const currencyKnowledgeBase = () => knowledgeBaseArticle('25692864101137');
export const dealPhasesKnowledgeBase = () => knowledgeBaseArticle('25691597918737');
export const quotationsKnowledgeBase = () => knowledgeBaseArticle('25692364929553');
export const commercialDiscountsKnowledgeBase = () => knowledgeBaseArticle('25690788116881');
export const vatRatesKnowledgeBase = () => knowledgeBaseArticle('25691580977681');
export const dealPhaseDateKnowledgeBase = () => knowledgeBaseArticle('25693825469585');
export const introducingTextKnowledgeBase = () => knowledgeBaseArticle('31592163024913');
export const emailSignatureKnowledgeBase = () => knowledgeBaseArticle('25690729809425');
export const emailBccKnowledgeBase = () => knowledgeBaseArticle('25693959243409');
export const closingDaysKnowledgeBase = () => knowledgeBaseArticle('25691773689873');
export const paymentTermsKnowledgeBase = () => knowledgeBaseArticle('25691448283537');
export const paymentMethodsKnowledgeBase = () => knowledgeBaseArticle('25693982414097');
export const sendByPostKnowledgeBase = () => knowledgeBaseArticle('25691544686225');
export const fiscalYearsKnowledgeBase = () => knowledgeBaseArticle('25691688862481');
export const invoiceNumberKnowledgeBase = () => knowledgeBaseArticle('25691346944657');
export const stockManagementKnowledgeBase = () => knowledgeBaseArticle('25691712397713');
export const priceListsKnowledgeBase = () => knowledgeBaseArticle('25691973505809');
export const priceUnitsKnowledgeBase = () => knowledgeBaseArticle('25691458413201');
export const sharedInboxKnowledgeBase = () => knowledgeBaseArticle('25691088759057');
export const autoRepliesKnowledgeBase = () => knowledgeBaseArticle('25691071132689');
export const replyTemplatesKnowledgeBase = () => knowledgeBaseArticle('25691055676433');
export const customStatusesKnowledgeBase = () => knowledgeBaseArticle('25691066527889');
export const teamKnowledgeBase = () => knowledgeBaseArticle('25690706850321');
export const hourlyRatesKnowledgeBase = () => knowledgeBaseArticle('25691956252177');
export const addScanExpensesKnowledgeBase = () => knowledgeBaseArticle('34057221210001');

export const howToAddCompaniesKnowledgeBase = () => knowledgeBaseArticleForIntroStates('25691985605777');
export const howToAddContactsKnowledgeBase = () => knowledgeBaseArticleForIntroStates('25691553397137');
export const howToAddProjectsKnowledgeBase = () => knowledgeBaseArticleForIntroStates('25695560896145');
export const howToManageExternalHolidaysKnowledgeBase = () => knowledgeBaseArticleForIntroStates('26537748973713');

const applyQueryParametersFromMap = (url: string, queryParameters: Record<string, string | number | boolean> = {}) => {
  const queryString = Object.entries(queryParameters)
    .map((entry) => entry.map(encodeURIComponent).join('='))
    .join('&');

  if (queryString) {
    return `${url}?${queryString}`;
  }

  return url;
};
export const welcomeToOurTemplateGalleryKnowledgeBase = (
  queryParametersAsMap: Record<string, string | number | boolean>,
) => {
  return applyQueryParametersFromMap(knowledgeBaseArticle('25693263841937'), queryParametersAsMap);
};
export const dealOverviewKnowledgeBase = () => knowledgeBaseArticle('25693446616721');
export const cloudSignKnowledgeBase = () => knowledgeBaseArticle('25690878232593');
export const dealDetailKnowledgeBase = () => knowledgeBaseArticle('25697814636305');
export const dealForecastKnowledgeBase = () => knowledgeBaseArticle('25697739813521');
export const teamleaderFocusForSalesKnowledgeBase = () => knowledgeBaseArticle('25692905494289');
export const projectRightsKnowledgeBase = () => knowledgeBaseArticle('25695680166801');
export const projectsWorkBreakdownKnowledgeBase = () => knowledgeBaseArticle('25695560896145');
export const projectCostsKnowledgeBase = () => knowledgeBaseArticle('31353971388561');
export const projectBudgetManagementKnowledgeBase = () => knowledgeBaseArticle('30424081377553');
export const projectPlanningKnowledgeBase = () => knowledgeBaseArticle('25696110215441');
export const exportReportsKnowledgeBase = () => knowledgeBaseArticle('25691716494225');
export const fileManagementKnowledgeBase = () => knowledgeBaseArticle('25697920323601');
export const copyProjectKnowledgeBase = () => knowledgeBaseArticle('27877954273297');
export const billProjectKnowledgeBase = () => knowledgeBaseArticle('25695672586001');
export const createInvoiceKnowledgeBase = () => knowledgeBaseArticle('25692519605905');
export const emailInvoiceKnowledgeBase = () => knowledgeBaseArticle('25692222317201');
export const setupInvoiceCloudKnowledgeBase = () => knowledgeBaseArticle('25692202362257');
export const bookkeepingIntegrationsKnowledgeBase = () => knowledgeBaseArticle('25691318213393');
export const invoiceRemindersKnowledgeBase = () => knowledgeBaseArticle('25691492744849');
export const invoiceForecastKnowledgeBase = () => knowledgeBaseArticle('25693585752209');
export const globalSearchKnowledgeBase = () => knowledgeBaseArticle('25697650949521');
export const customFieldsAmountKnowledgeBase = () => knowledgeBaseArticle('25691166486673');
export const daysOffTimeOrderIsIncorrectKnowledgeBase = () => knowledgeBaseArticle('25693164563601');
export const adminRightsKnowledgeBase = () => knowledgeBaseArticle('25691974365841');
export const grantAdminRightsKnowledgeBase = () => knowledgeBaseArticle('25690956768913');
export const combineLimitedCRMAndLimitedDealsKnowledgeBase = () => knowledgeBaseArticle('25691431419537');
export const documentLayoutMaxFileSizeKnowledgeBase = () => knowledgeBaseArticle('25691101412881');
export const reduceUsersOnSubscriptionKnowledgeBase = () => knowledgeBaseArticle('25692834076433');
export const teamleaderFocusWalletKnowledgeBase = () => knowledgeBaseArticle('25691506715921');
export const vatKvkSiretAutofillKnowledgeBase = () => knowledgeBaseArticle('25693332190737');
export const whiteLabelDomainsKnowledgeBase = () => knowledgeBaseArticle('25692624924561');
export const whiteLabelDomainsTroubleShootGuideKnowledgeBase = () => knowledgeBaseArticle('25695210286481');
export const emailFailedToDeliverTroubleShootGuideKnowledgeBase = () => knowledgeBaseArticle('25693895081617');
export const resourcePlanningKnowledgeBase = () => knowledgeBaseArticle('25696175894929');
export const addNextgenProjectKnowledgeBase = () => knowledgeBaseArticle('25695560896145');
export const limitedCRMAccessKnowledgeBase = () => knowledgeBaseArticle('25692271859857');
export const howToCustomizeTemplatesKnowledgeBase = () => knowledgeBaseArticle('25692647207057');
export const newUserKnowledgeBase = () => knowledgeBaseArticle('25690695274513');
export const userSettingsKnowledgeBase = () => knowledgeBaseArticle('25695511139345');
export const weekScheduleKnowledgeBase = () => knowledgeBaseArticle('25691870457873');
export const vacationKnowledgeBase = () => knowledgeBaseArticle('25690696602897');
export const userEmailKnowledgeBase = () => knowledgeBaseArticle('25693544882449');

export const nextgenProjectChecklistKnowledgeBase = () => knowledgeBaseArticle('25696220612369');
export const nextgenProjectRightsKnowledgeBase = () => knowledgeBaseArticle('25695680166801');
export const nextgenProjectCostsKnowledgeBase = () => knowledgeBaseArticle('31353971388561');
export const nextgenProjectBudgetManagementKnowledgeBase = () => knowledgeBaseArticle('30424081377553');
export const nextgenProjectPlanningKnowledgeBase = () => knowledgeBaseArticle('25696110215441');
export const nextgenProjectTemplatesKnowledgeBase = () => knowledgeBaseArticle('27877954273297');
export const nextgenProjectInvoicesKnowledgeBase = () => knowledgeBaseArticle('25695672586001');
export const nextgenProjectMigrationGuide = () => knowledgeBaseArticle('25696033231377');
export const nextgenProjectWebinar = () => knowledgeBaseArticle('33326819651857');

export const faqKnowledgeBase = () => knowledgeBaseCategory('24865511664273');
export const faqIntegrationsKnowledgeBase = () => knowledgeBaseCategory('24865511664273?folder=102');
export const videoTutorialsKnowledgeBase = () => knowledgeBaseCategory('24833571784977');
export const apiDocumentation = () => 'https://developer.focus.teamleader.eu';

export const wistiaPlayer = (videoId: string, controlsVisibleOnLoad: boolean = false, playButton: boolean = true) =>
  `https://fast.wistia.net/embed/iframe/${videoId}?controlsVisibleOnLoad=${controlsVisibleOnLoad}&playButton=${playButton}`;

export const mobileAppSplash = (platform?: MobilePlatform) => {
  return platform ? `/try-mobile-app#${platform}` : '/try-mobile-app';
};

export const mobileAppStore = (platform: MobilePlatform) => {
  switch (platform) {
    case MobilePlatform.Ios:
      return 'https://apps.apple.com/us/app/teamleader-focus/id6453793095';
    case MobilePlatform.Android:
      return 'https://play.google.com/store/apps/details?id=eu.teamleader.focus&referrer=utm_source%3Dknowledge_base%26utm_medium%3Dwebsite';
    default:
      return null;
  }
};

export const createUtmTags = (utmCampaign: string, utmMedium = 'referral', utmSource = 'teamleader') => {
  return `utm_campaign=${utmCampaign}&utm_medium=${utmMedium}&utm_source=${utmSource}`;
};
